import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Cpu, Zap, AlertTriangle, CheckCircle, Brain, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const ChatGPTPage = () => {
  const navigate = useNavigate();

  const benefits = [
    {
      icon: <Zap className="w-6 h-6" />,
      title: 'Instant Information',
      description: 'Get quick answers and explanations on a wide range of topics in seconds.'
    },
    {
      icon: <Brain className="w-6 h-6" />,
      title: 'Learning Assistant',
      description: 'Break down complex concepts, provide examples, and explain things in different ways.'
    },
    {
      icon: <MessageSquare className="w-6 h-6" />,
      title: 'Natural Conversation',
      description: 'Engage in human-like dialogue with context awareness and follow-up questions.'
    },
    {
      icon: <CheckCircle className="w-6 h-6" />,
      title: 'Multiple Use Cases',
      description: 'Writing assistance, code generation, analysis, brainstorming, and much more.'
    }
  ];

  const warnings = [
    {
      title: 'Not Always Accurate',
      description: 'ChatGPT can provide incorrect or outdated information. Always verify important facts from reliable sources.'
    },
    {
      title: 'No Real-Time Data',
      description: 'The model has a knowledge cutoff date and does not have access to current events or real-time information.'
    },
    {
      title: 'Potential Bias',
      description: 'AI models can reflect biases present in their training data. Be critical of responses, especially on sensitive topics.'
    },
    {
      title: 'Not a Replacement for Experts',
      description: 'For medical, legal, or financial advice, always consult qualified professionals.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar scrolled={true} />
      
      {/* Hero Section */}
      <section className="relative bg-[#0d6571] pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-white hover:text-white/80 mb-8 -ml-4"
          >
            <ArrowLeft className="mr-2" />
            Back to Home
          </Button>
          
          <div className="flex items-center gap-4 mb-6">
            <Cpu className="w-16 h-16 text-[#d97841]" />
            <h1 className="text-5xl md:text-6xl font-bold text-white">
              What is ChatGPT?
            </h1>
          </div>
          
          <p className="text-xl text-white/90 max-w-4xl leading-relaxed">
            Understanding the AI-powered conversational assistant and how to use it effectively
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          {/* What is ChatGPT */}
          <div className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Understanding ChatGPT
            </h2>
            <div className="bg-gradient-to-br from-[#0d6571]/5 to-[#0d6571]/10 border-l-4 border-[#0d6571] p-8 rounded-r-lg mb-8">
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                <strong>ChatGPT</strong> (Chat Generative Pre-trained Transformer) is a large language 
                model-based chatbot developed by OpenAI and launched on November 30, 2022.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                It enables users to refine and steer a conversation towards a desired length, format, 
                style, level of detail, and language. Successive prompts and replies, known as prompt 
                engineering, are considered at each conversation stage as context.
              </p>
            </div>
          </div>

          {/* How It Works */}
          <div className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
              How ChatGPT Works
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white border-2 border-gray-200 rounded-xl p-8">
                <div className="bg-[#0d6571] text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold mb-4">
                  1
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Training Process</h3>
                <p className="text-gray-600 leading-relaxed">
                  ChatGPT is trained on vast amounts of text data from the internet, books, and articles. 
                  It learns patterns, context, and relationships between words to generate human-like responses.
                </p>
              </div>
              
              <div className="bg-white border-2 border-gray-200 rounded-xl p-8">
                <div className="bg-[#0d6571] text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold mb-4">
                  2
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Response Generation</h3>
                <p className="text-gray-600 leading-relaxed">
                  When you input a prompt, ChatGPT analyzes the context and generates a response by 
                  predicting the most likely sequence of words based on its training.
                </p>
              </div>
              
              <div className="bg-white border-2 border-gray-200 rounded-xl p-8">
                <div className="bg-[#0d6571] text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold mb-4">
                  3
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Context Awareness</h3>
                <p className="text-gray-600 leading-relaxed">
                  The model maintains conversation history within a session, allowing it to understand 
                  follow-up questions and provide contextually relevant answers.
                </p>
              </div>
              
              <div className="bg-white border-2 border-gray-200 rounded-xl p-8">
                <div className="bg-[#0d6571] text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold mb-4">
                  4
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Continuous Learning</h3>
                <p className="text-gray-600 leading-relaxed">
                  While individual conversations don't update the model, user feedback helps improve 
                  future versions through reinforcement learning from human feedback (RLHF).
                </p>
              </div>
            </div>
          </div>

          {/* Benefits */}
          <div className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
              Benefits of Using ChatGPT
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-r from-white to-[#0d6571]/5 border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all duration-300 flex items-start gap-4"
                >
                  <div className="bg-[#d97841] text-white p-3 rounded-lg flex-shrink-0">
                    {benefit.icon}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{benefit.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Important Warnings */}
          <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-8 md:p-12">
            <div className="flex items-center gap-4 mb-8">
              <AlertTriangle className="w-12 h-12 text-red-600" />
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Important: What to Double Check
              </h2>
            </div>
            
            <div className="bg-white rounded-xl p-6 mb-6">
              <p className="text-xl font-bold text-red-600 mb-4">
                ⚠️ The information given by ChatGPT are not always facts. Always do your own research!
              </p>
            </div>

            <div className="space-y-6">
              {warnings.map((warning, index) => (
                <div key={index} className="bg-white rounded-xl p-6 border-l-4 border-red-400">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{warning.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{warning.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Best Practices */}
          <div className="mt-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
              Best Practices for Using ChatGPT
            </h2>
            <div className="bg-[#0d6571] rounded-2xl p-12 text-white">
              <ul className="space-y-4 text-lg">
                <li className="flex items-start">
                  <span className="text-[#d97841] mr-3 text-2xl">✓</span>
                  <span>Be specific and clear in your prompts for better results</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#d97841] mr-3 text-2xl">✓</span>
                  <span>Verify critical information from multiple reliable sources</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#d97841] mr-3 text-2xl">✓</span>
                  <span>Use it as a starting point for research, not the final answer</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#d97841] mr-3 text-2xl">✓</span>
                  <span>Ask follow-up questions to clarify or expand on responses</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#d97841] mr-3 text-2xl">✓</span>
                  <span>Be aware of the knowledge cutoff date and limitations</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#d97841] mr-3 text-2xl">✓</span>
                  <span>Don't share sensitive personal or confidential information</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ChatGPTPage;